import { DIRECT_ONLY } from './search-jump'

/**
 * Is the `?channel=` value in the URL one the loaded channel list cannot account for?
 *
 * <h2>The failure this exists to remove (bug-128)</h2>
 * `MessagesView` reads `?channel=` straight from the URL and hands it to a controlled
 * `<select>`. A controlled select whose value matches no `<option>` renders <strong>blank</strong>,
 * so with a hand-edited or stale shared link the control silently claims "no filter" while the
 * history, the rail and the export are all being fetched with the id that is still sitting in the
 * URL. The reader is shown an empty page and given no reason for it.
 *
 * <h2>Why the answer must wait for the list to LOAD</h2>
 * `useChannels` is asynchronous, so "is this id in the list?" is `false` for every id — including
 * every perfectly good one — for as long as the fetch is in flight. Answering `true` in that state
 * would clear the filter on <em>every</em> page load, which is not a robustness fix but the
 * destruction of the channel-jump feature: `ChannelsPage` navigates to
 * `/app/messages?channel=<id>`, and that link would land on an unfiltered page every time. Hence
 * `channelsLoaded`: unknown-ness is a claim that can only be made about a list that actually
 * arrived. While loading, and after a failed fetch, the URL is believed.
 *
 * <p>`none` ({@link DIRECT_ONLY}) is a legal value that is deliberately not a channel id, and is
 * never unknown.
 *
 * @param filter the raw `?channel=` value: `undefined` (everything), {@link DIRECT_ONLY}, or a
 *     channel id
 * @param channels the loaded channel list, or `undefined` while there is none
 * @param channelsLoaded whether the channel query has actually SUCCEEDED — not merely settled
 */
export function channelFilterIsUnknown(
  filter: string | undefined,
  channels: readonly { id: string }[] | undefined,
  channelsLoaded: boolean,
): boolean {
  if (filter === undefined) return false
  if (filter === DIRECT_ONLY) return false
  if (!channelsLoaded) return false
  return !(channels ?? []).some((channel) => channel.id === filter)
}
